<template>
  <SnackbarAlert />
  <ErroConexao v-if="mostrarErroConexao" />
  <v-container
    v-else
    class="py-12"
  >
    <TitleHeader>Links Importantes</TitleHeader>

    <v-list
      v-if="!loading && processedLinks.length"
      dense
      class="links-list"
    >
      <v-list-item
        v-for="(link, index) in processedLinks"
        :key="index"
        class="mb-6 link-item"
        :style="`animation-delay: ${index * 150}ms`"
        @click="link.url ? abrirLink(link.url) : null"
      >
        <v-list-item-title
          class="link-title"
          :class="{ clickable: !!link.url }"
        >
          {{ link.title }}
        </v-list-item-title>
        <v-list-item-subtitle
          v-if="link.url"
          class="link-url"
        >
          <a
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="link-anchor"
            @click.stop
          >
            {{ link.url }}
          </a>
        </v-list-item-subtitle>
      </v-list-item>
    </v-list>

    <template v-else-if="loading">
      <LoadingState text="Carregando..." />
    </template>

    <div
      v-else
      class="text-center text-medium-emphasis mt-6"
    >
      Nenhum link encontrado.
    </div>
  </v-container>
</template>

<script setup lang="ts">
import TitleHeader from '@/components/TitleHeader.vue'
import ErroConexao from '@/components/ErroConexao.vue'
import LoadingState from '@/components/LoadingState.vue'
import { ref, onMounted } from 'vue'
import { decode } from 'html-entities'
import { useMainStore } from '@/stores/index'

definePageMeta({
  middleware: 'manutencao',
  title: 'FUNARPEN - Links Importantes',
})

useHead({
  title: 'Links Importantes',
  meta: [{ name: 'description', content: 'Links Importantes' }],
})

const processedLinks = ref<{ title: string; url: string }[]>([])
const loading = ref(true)
const mostrarErroConexao = ref(false)
const mainStore = useMainStore()

const abrirLink = (url: string) => {
  if (!url) return
  window.open(url, '_blank')
}

const extractLinksFromHtml = (html: string) => {
  try {
    const links: { title: string; url: string }[] = []
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Versão mais robusta para encontrar parágrafos
    const elements = Array.from(doc.body.children)
    let currentTitle = ''

    elements.forEach((element) => {
      const text = element.textContent?.trim() || ''

      if (text.includes('[1]')) {
        if (currentTitle) {
          links.push({ title: currentTitle, url: '' })
        }
        currentTitle = decode(text.replace('[1]', '').trim())
      } else if (text.includes('[2]') && currentTitle) {
        const anchor = element.querySelector('a')
        let url = anchor?.getAttribute('href')?.trim() || ''

        // Se não tiver <a>, tenta pegar o link direto do texto (ex: [2]https://...)
        const match = text.match(/https?:\/\/\S+/)
        if (!url && match && match[0]) {
          url = match[0].trim()
        }

        links.push({ title: currentTitle, url })
        currentTitle = ''
      }
    })

    if (currentTitle) {
      links.push({ title: currentTitle, url: '' })
    }

    return links
  } catch (error) {
    console.error('Error processing HTML:', error)
    return []
  }
}

onMounted(async () => {
  if (!process.client) return

  try {
    const baseUrl = useRuntimeConfig().public.backofficeApiBaseUrl
    const endpoint = `${baseUrl}/portal/paginas/buscar-pagina?slug=funarpen-links-uteis`

    console.log('Fetching from:', endpoint)
    const res = await fetch(endpoint)

    if (!res.ok) {
      console.error('HTTP error:', res.status, res.statusText)
      throw new Error(`HTTP error! status: ${res.status}`)
    }

    const data = await res.json()
    console.log('API response:', data)

    // Encontra a descrição em qualquer lugar da resposta
    const htmlContent = findDescription(data)

    if (!htmlContent) {
      console.warn('No HTML content found in response')
      processedLinks.value = []
      return
    }

    processedLinks.value = extractLinksFromHtml(htmlContent)
  } catch (error) {
    console.error('Error loading links:', error)
    processedLinks.value = []
  } finally {
    loading.value = false
  }
})

// Função auxiliar para encontrar a descrição em diferentes estruturas
function findDescription(data: any): string {
  // Verifica vários caminhos possíveis
  const paths = ['resultado.descricao', 'data.resultado.descricao', 'descricao', 'content', 'data.content', 'resultado.content']

  for (const path of paths) {
    const value = path.split('.').reduce((obj, key) => obj?.[key], data)
    if (value) return value
  }

  return ''
}

onBeforeUnmount(() => {
  mainStore.resetSnackbar()
})
</script>

<style scoped>
.links-list {
  display: grid;
  gap: 14px;
  background: transparent;
}

.link-title {
  font-weight: 700;
  text-transform: uppercase;
  color: var(--funarpen-blue);
  font-size: 1.1rem;
  white-space: normal !important;
  word-break: break-word !important;
  transition: color 0.3s ease;
}

.link-title.clickable {
  cursor: pointer;
}

.link-title.clickable:hover {
  color: var(--funarpen-navy);
}

.link-url {
  color: rgba(16, 35, 52, 0.78);
  font-size: 0.95rem;
  line-height: 1.4;
  margin-top: 0.3rem;
  word-break: break-word !important;
}

.link-anchor {
  color: var(--funarpen-blue);
  text-decoration: none;
  transition: color 0.3s ease;
  display: inline-block;
  word-break: break-word !important;
}

.link-anchor:hover,
.link-anchor:focus-visible {
  color: var(--funarpen-blue-2);
  text-decoration: underline;
}

.link-item {
  padding: 1rem 1.15rem;
  border: 1px solid rgba(8, 47, 73, 0.12);
  border-left: 5px solid var(--funarpen-gold);
  border-radius: var(--funarpen-radius);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.96), rgba(248, 251, 253, 0.92)), #fff;
  box-shadow: var(--funarpen-shadow-soft);
  width: 100% !important;
  cursor: pointer;

  opacity: 0;
  animation: fadeInUp 0.5s forwards ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .link-title {
    font-size: 1rem !important;
  }

  .link-url {
    font-size: 0.9rem !important;
  }
}
</style>
